/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { OK, LEGACY_OK } from '../../constants/platformStatusCodes';
import arClient from '../../util/api_client';

function resolveWorkspaceStatusForUsageGuard(login) {
  const workspace = login?.workspaceData ?? login?.session?.workspace ?? null;
  if (workspace?.status != null) {
    return String(workspace.status).toLowerCase();
  }
  if (typeof window === 'undefined') return '';
  try {
    const raw = window.localStorage.getItem('session');
    if (!raw) return '';
    const s = JSON.parse(raw);
    const st = s?.workspace?.status;
    return st != null ? String(st).toLowerCase() : '';
  } catch {
    return '';
  }
}

const initialState = {
  invitations: [],
  invitationsLoading: false,
  members: [],
  membersPagination: null,
  membersLoading: false,
  workspacePlan: null,
  workspacePlanLoading: false,
  workspaceUsage: null,
  workspaceUsageLoading: false,
  workspaceSeats: null,
  workspaceSeatsLoading: false,
  roles: [],
  rolesLoading: false,
};

const WorkspaceSlice = createSlice({
  name: 'Workspace',
  initialState,
  reducers: {
    setInvitations(state, action) {
      state.invitations = action.payload;
    },
    setInvitationsLoading(state, action) {
      state.invitationsLoading = action.payload;
    },
    setMembers(state, action) {
      state.members = action.payload;
    },
    setMembersPagination(state, action) {
      state.membersPagination = action.payload;
    },
    setMembersLoading(state, action) {
      state.membersLoading = action.payload;
    },
    setWorkspacePlan(state, action) {
      state.workspacePlan = action.payload;
    },
    setWorkspacePlanLoading(state, action) {
      state.workspacePlanLoading = action.payload;
    },
    setWorkspaceUsage(state, action) {
      state.workspaceUsage = action.payload;
    },
    setWorkspaceUsageLoading(state, action) {
      state.workspaceUsageLoading = action.payload;
    },
    setWorkspaceSeats(state, action) {
      state.workspaceSeats = action.payload;
    },
    setWorkspaceSeatsLoading(state, action) {
      state.workspaceSeatsLoading = action.payload;
    },
    setRoles(state, action) {
      state.roles = action.payload;
    },
    setRolesLoading(state, action) {
      state.rolesLoading = action.payload;
    },
  },
});

export const {
  setInvitations,
  setInvitationsLoading,
  setMembers,
  setMembersPagination,
  setMembersLoading,
  setWorkspacePlan,
  setWorkspacePlanLoading,
  setWorkspaceUsage,
  setWorkspaceUsageLoading,
  setWorkspaceSeats,
  setWorkspaceSeatsLoading,
  setRoles,
  setRolesLoading,
} = WorkspaceSlice.actions;

export const getInvitations = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(setInvitationsLoading(true));
    const callback = (resp) => {
      if (resp.isCancelled) {
        dispatch(setInvitationsLoading(false));
        resolve(null);
        return;
      }
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        try {
          dispatch(setInvitationsLoading(false));
          const list = resp?.data ?? [];
          dispatch(setInvitations(Array.isArray(list) ? list : []));
          resolve(resp);
        } catch (e) {
          dispatch(setInvitationsLoading(false));
          reject(e);
        }
      } else {
        dispatch(setInvitationsLoading(false));
        reject(new Error(resp?.error || resp?.message || 'Failed to load invitations'));
      }
    };

    arClient.doApiCallGet('/workspace/invitations', callback);
  });
};

export const getMembers = ({ search, perPage = 10, page = 1 } = {}) => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(setMembersLoading(true));
    const callback = (resp) => {
      if (resp.isCancelled) {
        dispatch(setMembersLoading(false));
        resolve(null);
        return;
      }
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        try {
          dispatch(setMembersLoading(false));
          const payload = resp?.data ?? {};
          const list = Array.isArray(payload.data) ? payload.data : [];
          const pagination =
            payload.current_page != null
              ? {
                  current_page: payload.current_page,
                  per_page: payload.per_page,
                  total: payload.total,
                  last_page: payload.last_page,
                  from: payload.from,
                  to: payload.to,
                }
              : null;
          dispatch(setMembers(list));
          dispatch(setMembersPagination(pagination));
          resolve(resp);
        } catch (e) {
          dispatch(setMembersLoading(false));
          reject(e);
        }
      } else {
        dispatch(setMembersLoading(false));
        reject(new Error(resp?.error || resp?.message || 'Failed to load members'));
      }
    };

    const params = new URLSearchParams();
    if (search != null && String(search).trim()) params.set('search', search.trim());
    if (perPage !== undefined) params.set('per_page', perPage);
    if (page !== undefined) params.set('page', page);
    const query = params.toString();
    const endPoint = query ? `/workspace/members?${query}` : '/workspace/members';

    arClient.doApiCallGet(endPoint, callback);
  });
};

export const getWorkspacePlan = () => (dispatch) => {
  return new Promise((resolve) => {
    dispatch(setWorkspacePlanLoading(true));
    const callback = (resp) => {
      if (resp.isCancelled) {
        dispatch(setWorkspacePlanLoading(false));
        resolve(resp);
        return;
      }
      dispatch(setWorkspacePlanLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        try {
          const raw = resp?.data ?? {};
          const plan = raw.plan ?? null;
          const seats = raw.seats ?? null;
          dispatch(setWorkspacePlan(plan && seats ? { plan, seats } : null));
          resolve(resp);
        } catch (e) {
          dispatch(setWorkspacePlan(null));
          resolve(resp);
        }
      } else {
        dispatch(setWorkspacePlan(null));
        resolve(resp);
      }
    };

    arClient.doApiCallGet('/workspace/plan', callback);
  });
};

export const getUsage = () => (dispatch, getState) => {
  const login = getState()?.login;
  if (resolveWorkspaceStatusForUsageGuard(login) === 'hidden') {
    return Promise.resolve({ status: 'skipped', reason: 'workspace_hidden' });
  }

  return new Promise((resolve) => {
    dispatch(setWorkspaceUsageLoading(true));
    const callback = (resp) => {
      if (resp.isCancelled) {
        dispatch(setWorkspaceUsageLoading(false));
        resolve(resp);
        return;
      }
      dispatch(setWorkspaceUsageLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const raw = resp?.data;
        const usage = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
        dispatch(setWorkspaceUsage(usage));
        resolve(resp);
      } else {
        dispatch(setWorkspaceUsage(null));
        resolve(resp);
      }
    };

    arClient.doApiCallGet('/workspace/usage', callback);
  });
};

export const getWorkspaceSeats = () => (dispatch) => {
  return new Promise((resolve) => {
    dispatch(setWorkspaceSeatsLoading(true));
    const callback = (resp) => {
      if (resp.isCancelled) {
        dispatch(setWorkspaceSeatsLoading(false));
        resolve(resp);
        return;
      }
      dispatch(setWorkspaceSeatsLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const raw = resp?.data?.seats;
        const seats = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
        dispatch(setWorkspaceSeats(seats));
        resolve(resp);
      } else {
        dispatch(setWorkspaceSeats(null));
        resolve(resp);
      }
    };

    arClient.doApiCallGet('/workspace/seats', callback);
  });
};

export const getWorkspaceRoles = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(setRolesLoading(true));
    const callback = (resp) => {
      if (resp.isCancelled) {
        dispatch(setRolesLoading(false));
        resolve(null);
        return;
      }
      dispatch(setRolesLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        try {
          const list = resp?.data ?? [];
          dispatch(setRoles(Array.isArray(list) ? list : []));
          resolve(resp);
        } catch (e) {
          dispatch(setRoles([]));
          reject(e);
        }
      } else {
        dispatch(setRoles([]));
        reject(new Error(resp?.error || resp?.message || 'Failed to load roles'));
      }
    };

    arClient.doApiCallGet('/workspace/roles', callback);
  });
};

export const sendInvitation = (payload) => () => {
  return new Promise((resolve, reject) => {
    const body = {
      email: payload.email,
      role_id: payload.role_id,
      ...(payload.name != null && payload.name !== '' ? { name: payload.name } : {}),
    };
    const callback = (resp) => {
      if (resp.isCancelled) {
        reject(new Error('Request cancelled'));
        return;
      }
      if (
        resp.status === 201 ||
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200
      ) {
        resolve(resp);
        return;
      }
      const message =
        resp?.error || resp?.message || resp?.data?.message || 'Failed to send invitation';
      reject(new Error(message));
    };
    arClient.doApiCallV2('/workspace/invitations', body, callback);
  });
};

const invitationActionCallback = (resolve, reject) => (resp) => {
  if (resp.isCancelled) {
    reject(new Error('Request cancelled'));
    return;
  }
  if (
    resp.status === 201 ||
    resp.status === LEGACY_OK ||
    resp.status === OK ||
    resp.status === 200
  ) {
    resolve(resp);
    return;
  }
  const message = resp?.message || resp?.error || resp?.data?.message || 'Request failed';
  reject(new Error(message));
};

export const resendInvitation = (id) => () => {
  return new Promise((resolve, reject) => {
    arClient.doApiCallV2(
      `/workspace/invitations/${id}/resend`,
      {},
      invitationActionCallback(resolve, reject)
    );
  });
};

export const revokeInvitation = (id) => () => {
  return new Promise((resolve, reject) => {
    arClient.doApiCallV2(
      `/workspace/invitations/${id}/revoke`,
      {},
      invitationActionCallback(resolve, reject)
    );
  });
};

export const getInvitationDetail = (id) => () => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.isCancelled) {
        reject(new Error('Request cancelled'));
        return;
      }
      if (resp.status === 'ERROR') {
        reject(new Error(resp.error || resp.message || 'Failed to load invitation'));
        return;
      }
      resolve(resp);
    };
    arClient.doApiCallGet(`/workspace/invitations/${id}`, callback);
  });
};

const joinRequestMutationCallback = (resolve, reject) => (resp) => {
  if (resp.isCancelled) {
    reject(new Error('Request cancelled'));
    return;
  }
  if (
    resp.status === 201 ||
    resp.status === LEGACY_OK ||
    resp.status === OK ||
    resp.status === 200
  ) {
    resolve(resp);
    return;
  }
  const err = new Error(
    resp.message || resp.error || resp.errors?.role_id?.[0] || 'Request failed'
  );
  err.error_code = resp.error_code;
  err.responseData = resp;
  err.errors = resp.errors;
  err.meta = resp.meta;
  reject(err);
};

export const approveJoinRequest = (id, roleId) => () => {
  return new Promise((resolve, reject) => {
    arClient.doApiCallV2(
      `/workspace/invitations/${id}/approve`,
      { role_id: roleId },
      joinRequestMutationCallback(resolve, reject)
    );
  });
};

export const declineJoinRequest = (id, reason = '') => () => {
  return new Promise((resolve, reject) => {
    arClient.doApiCallV2(
      `/workspace/invitations/${id}/admin-decline`,
      { reason: reason != null ? String(reason).slice(0, 500) : '' },
      joinRequestMutationCallback(resolve, reject)
    );
  });
};

export const removeUserFromWorkspace = (id) => () => {
  return new Promise((resolve, reject) => {
    arClient.doApiCallV2(
      `/workspace/members/${id}/deactivate`,
      {},
      invitationActionCallback(resolve, reject)
    );
  });
};

export default WorkspaceSlice.reducer;
